/* App shell: a fixed 56px top bar above a row of [side rail | scrollable main
   content]. Mirrors andavo-dev's SharedWebNav layout shape.

   The shell is auth- and nav-agnostic — the consuming surface passes in the
   `user`, the sign-out action, and (optionally) its own nav model. By default
   the main content is the active route's <Outlet>; pass `children` to use the
   shell outside a router.

   RESPONSIVE NAV: on desktop (≥ lg) the SideNav is a fixed rail. On small
   viewports it collapses to an off-canvas DRAWER toggled by the TopBar
   hamburger — with a scrim, body-scroll-lock while open, and auto-close on route
   change (restores the behavior the standalone CRM had; now shared so every
   surface gets consistent mobile nav).

   CONTENT LAYOUT: <main> is the ONE main landmark for every route. By default
   it is 'padded' (the 24/32px gutter document-style screens are designed
   against, scrolling inside main). An app-style surface that owns its own
   columns and scroll regions (chat) asks for 'full-bleed': no gutter, no
   scroll on main, and only the safe-area insets kept (left, right, bottom). Such a surface must NOT
   render its own <main> or <nav> — the shell already provides both. */
import { type ReactNode, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import type { CurrentUser, NavItem } from '../types';
import { SideNav } from './SideNav';
import { TopBar } from './TopBar';

export type AppShellProps = {
  /** Authenticated user for the top-bar avatar/account menu. */
  user: CurrentUser;
  /** Sign-out action wired to the app's auth provider (e.g. MSAL logout). */
  onSignOut?: () => void;
  /** Primary-nav (Platform) entries. Defaults to the platform IA. */
  navItems?: NavItem[];
  /** Pinned-bottom entries (Settings / Support). Defaults to the standard set. */
  pinnedItems?: NavItem[];
  /** Main content. Omit to render the active route's <Outlet> (router usage). */
  children?: ReactNode;
  /**
   * 'padded' (default): the standard gutter; main scrolls.
   * 'full-bleed': the route fills the content area edge to edge and owns its
   * own scrolling (e.g. chat's conversation list + transcript).
   */
  contentLayout?: AppShellContentLayout;
};

export type AppShellContentLayout = 'padded' | 'full-bleed';

/* `pb-[…safe-area-inset-bottom]` keeps scrollable content (and any sticky
   footer a feature adds) clear of the iOS home indicator now that
   viewport-fit=cover lets content reach the physical screen edge;
   `pr-[…inset-right]` does the same for a landscape notch. The base 6/8 padding
   is preserved via max(). Full-bleed keeps ONLY the insets - all three, because
   below lg the nav is an off-canvas drawer, so main's left edge is the physical
   screen edge and a landscape notch sits there too. */
const MAIN_CLASSES: Record<AppShellContentLayout, string> = {
  padded:
    'flex-1 overflow-y-auto overscroll-contain bg-surface-background p-6 pr-[max(1.5rem,env(safe-area-inset-right))] pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:p-8 sm:pr-[max(2rem,env(safe-area-inset-right))] sm:pb-[max(2rem,env(safe-area-inset-bottom))]',
  'full-bleed':
    'flex min-w-0 flex-1 flex-col overflow-hidden bg-surface-background pr-[env(safe-area-inset-right)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]',
};

export function AppShell({
  user,
  onSignOut,
  navItems,
  pinnedItems,
  children,
  contentLayout = 'padded',
}: AppShellProps) {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  // Close the mobile drawer on route change.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the drawer is open (mobile only; harmless on desktop
  // where the drawer is never opened).
  useEffect(() => {
    if (!navOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [navOpen]);

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden bg-surface-background">
      <TopBar
        user={user}
        onSignOut={onSignOut}
        onOpenNav={() => setNavOpen(true)}
      />
      <div className="relative flex w-full flex-1 flex-row overflow-hidden">
        {/* Scrim — mobile only, behind the drawer, closes it on click. */}
        {navOpen && (
          <div
            data-testid="nav-scrim"
            aria-hidden="true"
            onClick={() => setNavOpen(false)}
            className="fixed inset-0 top-14 z-30 bg-neutral-900/40 lg:hidden"
          />
        )}
        <SideNav
          items={navItems}
          pinnedItems={pinnedItems}
          open={navOpen}
          onClose={() => setNavOpen(false)}
        />
        <main
          className={MAIN_CLASSES[contentLayout]}
          data-content-layout={contentLayout}
        >
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}
